import React from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components";
import { PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS } from "../../storiesPhase.definitions";
import { StoryEditForm } from "../StoryEditForm";
import type { StoryCardProps } from "./storyCard.types";

export default function StoryCard({
  story,
  index,
  epics,
  isEditing,
  onEdit,
  onDelete,
  onSave,
  onCancel
}: StoryCardProps) {
  if (isEditing) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-500">
        <StoryEditForm
          story={story}
          epics={epics}
          onSave={onSave}
          onCancel={onCancel}
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-500">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
              Story #{index + 1}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_LABELS[story.priority]?.color || "bg-yellow-100 text-yellow-800"}`}>
              {PRIORITY_LABELS[story.priority]?.label || story.priority}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${TYPE_LABELS[story.type]?.color || "bg-gray-100 text-gray-800"}`}>
              {TYPE_LABELS[story.type]?.label || story.type}
            </span>
            {story.storyPoints && (
              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                {story.storyPoints} pts
              </span>
            )}
          </div>
          
          <h4 className="font-medium text-gray-900 mb-1">
            {story.title}
          </h4>
          
          <p className="text-sm text-gray-600 mb-2">
            {story.description}
          </p>
          
          {(story.role && story.functionality && story.benefit) ? (
            <div className="text-sm text-gray-700 mb-2 bg-blue-50 p-2 rounded border-l-2 border-blue-300">
              <strong>Como</strong> {story.role}, <strong>eu quero</strong> {story.functionality}, <strong>para que</strong> {story.benefit}
            </div>
          ) : (
            <div className="text-xs text-amber-600 mb-2 bg-amber-50 p-2 rounded border-l-2 border-amber-300">
              ⚠️ Esta story precisa ser editada para incluir os campos: Como (role), eu quero (functionality), para que (benefit)
            </div>
          )}
          
          {story.acceptanceCriteria?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">Critérios de aceitação:</p>
              <div className="space-y-1">
                {(story.acceptanceCriteria || []).slice(0, 2).map((criteria, i) => (
                  <p key={criteria.id} className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {criteria.description}
                  </p>
                ))}
                {(story.acceptanceCriteria || []).length > 2 && (
                  <span className="text-xs text-gray-500">
                    +{(story.acceptanceCriteria || []).length - 2} mais critérios
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex space-x-2">
          <Button
            onClick={() => onEdit(story.id)}
            variant="ghost"
            size="sm"
          >
            Editar
          </Button>
          <Button
            onClick={() => onDelete(story.id)}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            leftIcon={<TrashIcon className="h-4 w-4" />}
          >
            Remover
          </Button>
        </div>
      </div>
    </div>
  );
}